import { templatePath } from '../path.ts'
import type { LoggableEvent } from '../recorder.ts'

export interface NetworkTapOptions {
  window: Window
  log: (event: LoggableEvent) => void
  now: () => number
  /** The host decides which calls are the app's own (e.g. its backend), so analytics and storage uploads stay out. */
  shouldRecord: (url: string) => boolean
}

const CORRELATION_HEADER = 'x-harmony-correlation-id'

/**
 * Wraps fetch to log what the app asked its backend for: method, templated path, status, duration and
 * the correlation id. It never reads a request or response body, header values other than the
 * correlation id, or the query string (EC28).
 */
export function startNetworkTap({ window, log, now, shouldRecord }: NetworkTapOptions): () => void {
  const original = window.fetch

  const tapped = async function (this: unknown, input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    if (!shouldRecord(url)) return original.call(window, input, init)

    const method = (init?.method ?? (typeof input === 'object' && 'method' in input ? input.method : undefined) ?? 'GET').toUpperCase()
    const path = templatePath(url)
    const startedAt = now()
    try {
      const response = await original.call(window, input, init)
      log({
        type: 'request',
        method,
        path,
        status: response.status,
        durationMs: Math.round(now() - startedAt),
        ...withCorrelation(response),
      })
      return response
    } catch (error) {
      log({ type: 'request', method, path, status: 0, durationMs: Math.round(now() - startedAt) })
      throw error
    }
  }

  window.fetch = tapped as typeof fetch
  return () => {
    if (window.fetch === (tapped as typeof fetch)) window.fetch = original
  }
}

function withCorrelation(response: Response): { correlationId?: string } {
  const id = response.headers.get(CORRELATION_HEADER)
  return id ? { correlationId: id } : {}
}
